/**
 * ICE servers for phop's WebRTC connections: fetched from the site's
 * `/api/turn` Pages Function (functions/api/turn.ts: Cloudflare STUN plus
 * short-lived TURN credentials, so players behind strict NATs can connect)
 * before joining a room. Anything else — no function (dev server, a plain
 * static server), an error, a timeout, a malformed answer — yields
 * `undefined`, and phop falls back to its default STUN server.
 */

export const ICE_ENDPOINT = '/api/turn';
export const ICE_TIMEOUT_MS = 3000;
/**
 * How often a room re-fetches its ICE servers: half the TURN credentials'
 * 1 h TTL (functions/api/turn.ts `TURN_TTL_SECONDS`), so a long lobby
 * session's new peer connections (a later match, a late joiner, a host
 * migration) never use expired TURN credentials.
 */
export const ICE_REFRESH_MS = 30 * 60 * 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isIceServer(value: unknown): value is RTCIceServer {
  if (!isRecord(value)) return false;
  const { urls, username, credential } = value;
  const urlsOk =
    typeof urls === 'string' ||
    (Array.isArray(urls) && urls.length > 0 && urls.every((u) => typeof u === 'string'));
  return (
    urlsOk &&
    (username === undefined || typeof username === 'string') &&
    (credential === undefined || typeof credential === 'string')
  );
}

/** `{ iceServers: [...] }` with at least one valid server, else null. */
export function parseIceServers(data: unknown): RTCIceServer[] | null {
  if (!isRecord(data) || !Array.isArray(data.iceServers)) return null;
  const servers = data.iceServers;
  return servers.length > 0 && servers.every(isIceServer) ? servers : null;
}

/** The `rtcConfig` for phop's `<Room>`, or `undefined` for phop's default STUN. */
export async function fetchRtcConfig(
  fetchFn: typeof fetch = fetch,
  timeoutMs = ICE_TIMEOUT_MS
): Promise<RTCConfiguration | undefined> {
  try {
    // The signal also covers reading the body, so a stalled response ends too.
    const res = await fetchFn(ICE_ENDPOINT, {
      cache: 'no-store',
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!res.ok) return undefined;
    const iceServers = parseIceServers(await res.json());
    return iceServers ? { iceServers } : undefined;
  } catch {
    return undefined; // network error, timeout, or not JSON (a dev server's index.html)
  }
}
